import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, Brain, Bot, Cpu, Database, Zap, ArrowRight, MessageSquare } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { useSEO } from "@/components/common/SEO";
import { Reveal } from "@/components/common/Reveal";
import { SectionLabel, CtaBanner } from "@/pages/Home";

const CAPABILITIES = [
  { icon: Bot, keyId: "assistants" },
  { icon: Database, keyId: "rag" },
  { icon: Zap, keyId: "automation" },
  { icon: Brain, keyId: "vision" },
  { icon: Cpu, keyId: "predictive" },
  { icon: MessageSquare, keyId: "conversational" },
];

const USE_CASES = ["support", "sales", "operations", "publicSector"];

export default function HosysAI() {
  const { lang, t } = useI18n();
  const L = (p) => `/${lang}${p}`;
  useSEO({
    lang,
    title: `HOSYS AI | HO-SYS Advanced Technologies`,
    description: t("hosysAiPage.subtitle"),
    path: "/hosys-ai",
  });

  return (
    <div className="pt-32">
      {/* Hero */}
      <section className="relative max-w-7xl mx-auto px-5 sm:px-8 pb-16 overflow-hidden">
        <div className="absolute -top-10 -right-24 w-[520px] h-[520px] rounded-full blur-3xl opacity-30 bg-neon/40 pointer-events-none" />
        <Reveal>
          <div className="inline-flex items-center gap-2 rounded-full border border-neon/40 bg-neon/10 px-3 py-1">
            <Sparkles className="w-4 h-4 text-neon" />
            <span className="text-xs font-mono uppercase tracking-widest text-neon">
              HOSYS AI
            </span>
          </div>
          <h1 className="mt-5 font-display font-extrabold tracking-tighter text-white text-4xl sm:text-6xl leading-[1.02] max-w-4xl">
            {t("hosysAiPage.title")}
          </h1>
          <p className="mt-6 text-white/60 text-lg md:text-xl max-w-2xl">
            {t("hosysAiPage.subtitle")}
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to={L("/contact")}
              data-testid="hosys-ai-cta-demo"
              className="inline-flex items-center gap-2 rounded-full bg-neon text-black font-semibold px-6 py-3 hover:bg-white transition-colors"
            >
              {t("hosysAiPage.ctaDemo")}
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to={L("/projects")}
              data-testid="hosys-ai-cta-cases"
              className="inline-flex items-center gap-2 rounded-full border border-white/20 text-white font-semibold px-6 py-3 hover:border-neon hover:text-neon transition-colors"
            >
              {t("hosysAiPage.ctaCases")}
            </Link>
          </div>
        </Reveal>
      </section>

      {/* Capabilities */}
      <section
        data-testid="hosys-ai-capabilities"
        className="max-w-7xl mx-auto px-5 sm:px-8 pb-20"
      >
        <Reveal>
          <SectionLabel>{t("hosysAiPage.capabilitiesLabel")}</SectionLabel>
          <h2 className="mt-3 font-display font-bold text-white text-3xl md:text-4xl max-w-3xl">
            {t("hosysAiPage.capabilitiesTitle")}
          </h2>
        </Reveal>
        <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {CAPABILITIES.map((c, i) => {
            const IconComp = c.icon;
            return (
              <Reveal key={c.keyId} delay={(i % 3) * 0.05}>
                <motion.div
                  whileHover={{ y: -4 }}
                  data-testid={`capability-${c.keyId}`}
                  className="h-full rounded-2xl border border-white/10 bg-[#0A0A0A] p-6 hover:border-neon/40 transition-colors"
                >
                  <div className="w-11 h-11 rounded-xl bg-neon/10 border border-neon/25 flex items-center justify-center mb-4">
                    <IconComp className="w-5 h-5 text-neon" />
                  </div>
                  <h3 className="font-display text-lg font-semibold text-white">
                    {t(`hosysAiPage.capabilities.${c.keyId}.title`)}
                  </h3>
                  <p className="mt-2 text-white/55 text-sm leading-relaxed">
                    {t(`hosysAiPage.capabilities.${c.keyId}.desc`)}
                  </p>
                </motion.div>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* Use cases */}
      <section
        data-testid="hosys-ai-use-cases"
        className="max-w-7xl mx-auto px-5 sm:px-8 pb-24"
      >
        <Reveal>
          <SectionLabel>{t("hosysAiPage.useCasesLabel")}</SectionLabel>
          <h2 className="mt-3 font-display font-bold text-white text-3xl md:text-4xl max-w-3xl">
            {t("hosysAiPage.useCasesTitle")}
          </h2>
        </Reveal>
        <div className="mt-10 grid md:grid-cols-2 gap-4">
          {USE_CASES.map((k, i) => (
            <Reveal key={k} delay={(i % 2) * 0.06}>
              <div
                data-testid={`use-case-${k}`}
                className="rounded-2xl border border-white/10 bg-[#0A0A0A] p-7 hover:border-neon/40 transition-colors"
              >
                <span className="font-mono text-xs uppercase tracking-widest text-neon">
                  {t(`hosysAiPage.useCases.${k}.tag`)}
                </span>
                <h3 className="mt-2 font-display text-xl font-semibold text-white">
                  {t(`hosysAiPage.useCases.${k}.title`)}
                </h3>
                <p className="mt-3 text-white/60 leading-relaxed">
                  {t(`hosysAiPage.useCases.${k}.desc`)}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <CtaBanner />
    </div>
  );
}
